/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Quick Schedule Form styles
 *
 * @file QuickScheduleFormStyles.js
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import { styled } from '@mui/material/styles';
import { Button, Typography } from '@mui/material';
import mixins from '../../../assets/styles/mixins.scss';
import {
  createPortalSlotStyles,
  createScopedClasses,
  createScopedStyles
} from '../../../assets/styles/createScopedStyles';

const QuickScheduleFormStyles = () => ({
  formPane: {
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 16,
    padding: 24,
    width: '100%',
    boxSizing: 'border-box',
    minWidth: 0,

    [mixins['md-sm-maxwidth']]: {
      padding: 16,
      borderRadius: 12
    }
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginBottom: 24
  },
  sectionTitle: {
    fontSize: mixins.fontSize18,
    fontWeight: mixins.fontWeight600,
    lineHeight: '28px',
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    margin: 0
  },
  sectionDescription: {
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    color: mixins.zambezi,
    fontFamily: mixins.fontFamilyPlusSans,
    margin: 0
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    width: '100%'
  },
  formBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%'
  },
  dueTimeSection: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'flex-end',
    width: 448,
    maxWidth: '100%',

    [mixins['md-sm-maxwidth']]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      width: '100%'
    }
  },
  dueTimeField: {
    flex: '1 1 0',
    minWidth: 0
  },
  timeZoneField: {
    flex: '1 1 0',
    minWidth: 0
  },
  dateRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'flex-start',
    width: '100%',
    maxWidth: '100%'
  },
  dateField: {
    minWidth: 220,
    flex: '0 0 220px',

    [mixins['md-sm-maxwidth']]: {
      flex: '1 1 100%',
      minWidth: 0,
      width: '100%'
    }
  },
  liveRegion: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0
  },
  actionsRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    width: '100%',

    [mixins['md-sm-maxwidth']]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 4
    }
  },
  cancelButton: {
    borderRadius: 1000,
    border: `2px solid ${mixins.lightTextColor2}`,
    color: mixins.lightTextColor2,
    padding: '16px 24px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    backgroundColor: 'transparent',
    maxHeight: 48,
    minWidth: 'unset',

    '& span': {
      minWidth: 104,
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center'
    },

    '&:hover, &.MuiButton-text:hover': {
      backgroundColor: mixins.lightThemeBgColor2,
      background: mixins.lightThemeBgColor2
    },

    [mixins['md-sm-maxwidth']]: {
      width: '100%',
      minWidth: 0
    }
  },
  saveButton: {
    borderRadius: 1000,
    backgroundColor: `${mixins.amethyst} !important`,
    color: `${mixins.chalkWhiteFont} !important`,
    padding: '16px 24px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    maxHeight: 48,
    minWidth: 'unset',
    border: 'none',

    '&.MuiButton-text, &.MuiButton-textPrimary': {
      backgroundColor: `${mixins.amethyst} !important`,
      background: `${mixins.amethyst} !important`,
      color: `${mixins.chalkWhiteFont} !important`
    },

    '& span': {
      width: '100%',
      display: 'inherit',
      minWidth: 104,
      alignItems: 'inherit',
      justifyContent: 'inherit'
    },

    '&:hover, &.MuiButton-text:hover': {
      backgroundColor: `${mixins.amethystColorV2} !important`,
      background: `${mixins.amethystColorV2} !important`,
      color: `${mixins.chalkWhiteFont} !important`
    },
    '&:disabled, &.Mui-disabled': {
      backgroundColor: `${mixins.amethystLightColor} !important`,
      background: `${mixins.amethystLightColor} !important`,
      color: `${mixins.chalkWhiteFont} !important`
    },

    [mixins['md-sm-maxwidth']]: {
      width: '100%',
      minWidth: 0
    }
  },
  alertModalPaper: {
    borderRadius: 24,
    padding: 48,
    width: 546,
    maxWidth: 546,
    minWidth: 546,
    minHeight: 'auto',
    boxSizing: 'border-box',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(13, 0, 77, 0.04), 0 1px 0.5px rgba(13, 0, 77, 0.1), 0 2px 0.5px rgba(13, 0, 77, 0.15)',

    [mixins['md-sm-maxwidth']]: {
      width: 'calc(100vw - 32px)',
      maxWidth: 'calc(100vw - 32px)',
      minWidth: 0,
      padding: 24,
      borderRadius: 16
    }
  },
  alertModalCloseButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    padding: 4,
    color: mixins.color_text_primary,
    height: 32,
    width: 32
  },
  alertModalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24
  },
  alertModalTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 24,
    fontWeight: mixins.fontWeight500,
    lineHeight: '32px',
    letterSpacing: '-0.48px',
    color: mixins.color_text_primary,
    margin: 0
  },
  alertModalMessage: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    lineHeight: '24px',
    color: mixins.color_text_primary,
    margin: 0,
    maxWidth: 450,

    [mixins['md-sm-maxwidth']]: {
      maxWidth: '100%'
    }
  },
  alertModalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    maxWidth: 450,

    [mixins['md-sm-maxwidth']]: {
      maxWidth: '100%'
    }
  },
  alertModalDoneButton: {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
    padding: '16px 24px',
    borderRadius: 1000,
    border: 'none',
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: mixins.amethystColorV2
    },
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      outlineOffset: 2
    },

    [mixins['md-sm-maxwidth']]: {
      width: '100%'
    }
  }
});

export const classes = createScopedClasses('aet-quick-schedule-form', QuickScheduleFormStyles());

export const scopedStyles = createScopedStyles(QuickScheduleFormStyles(), classes);

export const QuickScheduleFormStylesRoot = styled('div')(scopedStyles);

export const QuickScheduleCancelButton = styled(Button)({
  '&&': QuickScheduleFormStyles().cancelButton
});

export const QuickScheduleSaveButton = styled(Button)({
  '&&': QuickScheduleFormStyles().saveButton
});

/**
 * The date-range alert renders in a dialog portal, outside the form pane, so
 * its text elements carry the scope themselves.
 */
export const QuickScheduleFormStylesText = styled(Typography)(scopedStyles);

/**
 * These three slots land on elements owned by `CustomModal`, inside its dialog
 * portal, so they cannot be reached by a scope this module controls.
 */
export const portalSlotStyles = createPortalSlotStyles(QuickScheduleFormStyles(), classes, [
  'alertModalPaper',
  'alertModalContent',
  'alertModalCloseButton'
], 3);

export default QuickScheduleFormStyles;
